import { useState } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { brandInfo, articles } from '@/mocks/data';

const testimonials = [
  {
    quote: '久久在线的视频服务太流畅了，出差在外用手机也能高清观看，完全不卡顿！',
    author: '李女士',
    role: '市场总监',
  },
  {
    quote: '我们团队用久久在线办公套件后，协作效率至少提升了50%，推荐给所有远程团队。',
    author: '王先生',
    role: '创业公司CEO',
  },
  {
    quote: '云存储的空间够大，上传下载速度也快，而且价格比同类产品便宜不少。',
    author: '赵同学',
    role: '研究生',
  },
  {
    quote: '安全服务让我很放心，有一次账号异常登录立刻收到提醒，及时处理避免损失。',
    author: '陈女士',
    role: '自由职业者',
  },
  {
    quote: '久久在线的智能工具真的帮我节省了大量时间，AI写作助手特别好用。',
    author: '刘先生',
    role: '内容创作者',
  },
  {
    quote: '作为一家中小企业，久久在线企业版完美解决了我们的远程协作需求。',
    author: '周女士',
    role: '中小企业主',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    setIsSubmitting(true);
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9re5d4nlsngrm4lmb20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于久久在线',
    description: brandInfo.description,
    url: 'https://www.jiujiuzaixian.com/about',
    about: {
      '@type': 'Organization',
      name: brandInfo.name,
      description: brandInfo.description,
      foundingDate: brandInfo.founded,
      url: 'https://www.jiujiuzaixian.com',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: brandInfo.contact.phone,
        email: brandInfo.contact.email,
        contactType: 'customer service',
        availableLanguage: 'Chinese',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: brandInfo.contact.address,
      },
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于我们 - 久久在线 | 久久在线官网"
        description={brandInfo.description}
        keywords="久久在线,关于久久在线,久久在线品牌,久久在线团队,久久在线联系方式"
        canonicalUrl="https://www.jiujiuzaixian.com/about"
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            </div>

            <section className="mb-16 md:mb-20">
              <div className="max-w-3xl">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-6">
                  关于<span className="text-primary-600">久久在线</span>
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {brandInfo.description}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们的愿景是<strong>{brandInfo.vision}</strong>，使命是<strong>{brandInfo.mission}</strong>。
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-10">
                {[
                  { value: brandInfo.users, label: '服务用户' },
                  { value: brandInfo.employees + '+', label: '团队成员' },
                  { value: brandInfo.partners + '+', label: '合作伙伴' },
                  { value: '9年', label: '行业深耕' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-2xl bg-background-100 border border-background-200/50">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">{stat.value}</div>
                    <div className="text-xs md:text-sm text-foreground-600 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16 md:mb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                核心<span className="text-primary-600">价值观</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {brandInfo.values.map((value) => (
                  <div key={value.title} className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600 mb-4">
                      <i className={`${value.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{value.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section id="milestones" className="mb-16 md:mb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                发展<span className="text-primary-600">历程</span>
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 -translate-x-1/2 hidden md:block"></div>
                <div className="space-y-6 md:space-y-0">
                  {brandInfo.milestones.map((milestone, index) => (
                    <div
                      key={milestone.year}
                      className={`relative flex flex-col md:flex-row items-start gap-4 md:gap-0 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="md:w-1/2 md:px-8">
                        <div className={`p-5 rounded-2xl bg-background-50 border border-background-200/70 ${
                          index % 2 === 0 ? 'md:text-right' : 'md:text-left'
                        }`}>
                          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-2">
                            {milestone.year}
                          </span>
                          <h3 className="text-base font-semibold text-foreground-900 mb-1">{milestone.title}</h3>
                          <p className="text-sm text-foreground-600">{milestone.description}</p>
                        </div>
                      </div>
                      <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-primary-500 border-4 border-background-50 mt-6 z-10"></div>
                      <div className="md:w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-16 md:mb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                核心<span className="text-primary-600">团队</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {brandInfo.team.map((member) => (
                  <div key={member.name} className="p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden bg-background-200">
                      <img
                        src={member.image}
                        alt={member.name}
                        title={`${member.name} - ${member.role}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900">{member.name}</h3>
                    <p className="text-sm text-primary-600 font-medium mb-2">{member.role}</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">{member.bio}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16 md:mb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                用户<span className="text-primary-600">口碑</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {testimonials.map((t, i) => (
                  <div key={i} className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{t.quote}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold">
                        {t.author.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{t.author}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="contact" className="mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                <span className="text-primary-600">联系</span>我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">客服热线</p>
                        <p className="text-sm text-foreground-600">{brandInfo.contact.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                        <p className="text-sm text-foreground-600">{brandInfo.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-50 text-secondary-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">公司地址</p>
                        <p className="text-sm text-foreground-600">{brandInfo.contact.address}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                      给我们留言
                    </h3>

                    {formStatus === 'success' ? (
                      <div className="text-center py-10">
                        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                          <i className="ri-check-line text-2xl"></i>
                        </div>
                        <p className="text-foreground-900 font-medium">感谢您的留言！</p>
                        <p className="text-sm text-foreground-600 mt-1">我们会在1-2个工作日内与您联系。</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                        <div className="honeypot-field">
                          <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                              姓名 <span className="text-primary-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="contact-name"
                              name="name"
                              required
                              className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                              placeholder="请输入您的姓名"
                            />
                          </div>
                          <div>
                            <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                              邮箱 <span className="text-primary-500">*</span>
                            </label>
                            <input
                              type="email"
                              id="contact-email"
                              name="email"
                              required
                              className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                              placeholder="请输入您的邮箱"
                            />
                          </div>
                        </div>
                        <div>
                          <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            主题
                          </label>
                          <input
                            type="text"
                            id="contact-subject"
                            name="subject"
                            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                            placeholder="请输入留言主题"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            留言内容 <span className="text-primary-500">*</span>
                          </label>
                          <textarea
                            id="contact-message"
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                            placeholder="请输入您的留言内容（最多500字）"
                          ></textarea>
                        </div>

                        {formStatus === 'error' && formError && (
                          <div className="p-3 rounded-lg bg-accent-50 border border-accent-200 text-sm text-accent-800">
                            {formError}
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {isSubmitting ? '提交中...' : '提交留言'}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}